/**
 * Parser for `Broadcast.show_notes`, the DJ-entered episode commentary. The
 * field supports a deliberately tiny markdown subset — `**bold**`, `*italic*`
 * (asterisks only; underscores stay literal), single newlines as hard line
 * breaks (DJs use them for track lists), and blank lines as paragraph breaks.
 * Everything else renders as literal text.
 *
 * Kept in sync by hand with the app's copy (streamlover-app
 * src/lib/showNotes.ts) — change both together.
 */

export interface NotesSpan {
  text: string;
  bold: boolean;
  italic: boolean;
}

/** One hard line: the pieces between single newlines. */
export type NotesLine = NotesSpan[];

/** One paragraph: the lines between blank-line breaks. */
export type NotesParagraph = NotesLine[];

const BOLD = /\*\*(.+?)\*\*/g;
const ITALIC = /\*([^*\n]+)\*/g;

const parseItalic = (text: string, bold: boolean): NotesSpan[] => {
  const spans: NotesSpan[] = [];
  let last = 0;
  ITALIC.lastIndex = 0;
  for (let m = ITALIC.exec(text); m; m = ITALIC.exec(text)) {
    if (m.index > last) {
      spans.push({ text: text.slice(last, m.index), bold, italic: false });
    }
    spans.push({ text: m[1], bold, italic: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    spans.push({ text: text.slice(last), bold, italic: false });
  }
  return spans;
};

const parseLine = (line: string): NotesLine => {
  const spans: NotesSpan[] = [];
  let last = 0;
  BOLD.lastIndex = 0;
  for (let m = BOLD.exec(line); m; m = BOLD.exec(line)) {
    if (m.index > last) {
      spans.push(...parseItalic(line.slice(last, m.index), false));
    }
    // Italic still applies inside bold: `**a *b* c**`.
    spans.push(...parseItalic(m[1], true));
    last = m.index + m[0].length;
  }
  if (last < line.length) {
    spans.push(...parseItalic(line.slice(last), false));
  }
  return spans;
};

/** Split notes into paragraphs of lines of styled spans. */
export const parseShowNotes = (text: string): NotesParagraph[] =>
  text
    .replace(/\r\n?/g, "\n")
    .trim()
    .split(/\n{2,}/)
    .filter((paragraph) => paragraph.trim().length > 0)
    .map((paragraph) => paragraph.split("\n").map(parseLine));
